import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import { fetchBranch, fetchDeliveryCharges } from "../../../store/actions/store";
import { sortContextBranches } from "../../../checkout-v2/domain/contextBranches";
import "./MenuBranchSelector.css";

// Menu-only branch selection uses the existing menu endpoint, not checkout
// context validation (which requires delivery/pickup details and a time).
export const selectMenuBranch = (branchId) => (dispatch, getState) => {
  const state = getState();
  return dispatch(fetchBranch("pickup", branchId, state.cart.cart || [],
    state.cart.applied_promo || [], null, null, null, branchId, null, {
      preserveCart: true,
      throwOnError: true,
      beforeApply: () => {
        dispatch({ type: "SET_BRANCH_DELIVERY_CHARGE", payload: {
          branch: branchId, bdc: null, isPickup: true,
        } });
        dispatch({ type: "MODIFY_ADDRESS", payload: {
          selectedDeliveryType: 1, pickupArea: branchId, area: null, country: null,
        } });
      },
    }));
};

export const MenuBranchSelector = ({ branches = [], branchesLoaded = true, countryCode, loadBranches,
  selectedBranch, language = "english", settings = {}, onSelect }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [attemptedBranch, setAttemptedBranch] = useState(null);
  const [optionsError, setOptionsError] = useState(false);
  useEffect(() => {
    let active = true;
    if (settings.is_branch_based_ordering && !branchesLoaded && loadBranches)
      Promise.resolve(loadBranches(countryCode)).then(result => {
        if (active) setOptionsError(!result);
      }).catch(() => { if (active) setOptionsError(true); });
    return () => { active = false; };
  }, [settings.is_branch_based_ordering, branchesLoaded, loadBranches, countryCode]);
  const arabic = language === "arabic";
  const options = sortContextBranches(branches.filter(branch =>
    branch.is_deleted !== true && branch.is_active !== false && branch.enable_pickup !== false,
  ), language);
  if (!settings.is_branch_based_ordering) return null;
  if (optionsError) return <div className="sf-menu-branch" role="alert">
    <button type="button" onClick={async () => {
      try { setOptionsError(!await loadBranches(countryCode)); }
      catch (_) { setOptionsError(true); }
    }}>{arabic ? "إعادة تحميل الفروع" : "Retry loading branches"}</button>
  </div>;
  if (options.length < 2) return null;
  const selected = options.find(branch => String(branch.id) === String(selectedBranch));
  const change = async value => {
    const branchId = Number(value);
    if (loading || !branchId || (!error && String(branchId) === String(selectedBranch))) return;
    setLoading(true);
    setError(false);
    setAttemptedBranch(branchId);
    try { await onSelect(branchId); }
    catch (_) { setError(true); }
    finally { setLoading(false); }
  };
  return (
    <section className="sf-menu-branch" dir={arabic ? "rtl" : "ltr"}
      style={{ "--menu-branch-accent": settings.dark || "#33465b" }}>
      <label className="sf-menu-branch__control">
        <StorefrontOutlinedIcon aria-hidden="true" />
        <span>{arabic ? "قائمة الفرع" : "Branch menu"}</span>
        <select aria-label={arabic ? "اختر الفرع" : "Choose branch"}
          disabled={loading} value={selected?.id || ""} onChange={event => change(event.target.value)}>
          <option value="" disabled>{arabic ? "اختر الفرع" : "Choose branch"}</option>
          {options.map(branch => <option key={branch.id} value={branch.id}>
            {(arabic ? branch.ar_name || branch.name_ar : branch.name) || branch.name || branch.ar_name}
          </option>)}
        </select>
      </label>
      {loading && <small role="status">{arabic ? "جاري تحميل قائمة الفرع…" : "Loading branch menu…"}</small>}
      {error && <small role="alert">
        {arabic ? "تعذر تحميل القائمة. " : "Couldn’t load the menu. "}
        <button type="button" onClick={() => change(attemptedBranch)}>{arabic ? "إعادة المحاولة" : "Retry"}</button>
      </small>}
    </section>
  );
};

export default connect(state => ({
  branches: state.store.store.branches || [],
  branchesLoaded: state.store.fetchedDeliveryCharges,
  countryCode: state.store.store.country_code,
  selectedBranch: state.branch.branch,
  settings: state.store.store.settings,
  language: state.store.language,
}), { onSelect: selectMenuBranch,
  loadBranches: countryCode => fetchDeliveryCharges(countryCode, { validateMapPin: false }),
})(MenuBranchSelector);
